import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { UserRound, FlaskConical, Layers, HeartHandshake, ArrowUpRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Reveal, Chapter } from "@/components/Reveal";

const IMGS = {
  compassion: "https://images.pexels.com/photos/5206916/pexels-photo-5206916.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800",
  science: "https://images.pexels.com/photos/37839843/pexels-photo-37839843.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800",
  cells: "https://images.pexels.com/photos/11516492/pexels-photo-11516492.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  room: "https://images.pexels.com/photos/3736279/pexels-photo-3736279.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800",
};

const Container = ({ children, className = "" }) => (
  <div className={`max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 ${className}`}>{children}</div>
);

/* ---------------- INTRO ---------------- */
export const Intro = () => {
  const { t } = useSite();
  return (
    <section id="about" className="relative bg-offwhite py-24 sm:py-32">
      <Container>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-7">
            <Reveal><Chapter>{t.intro.chapter}</Chapter></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-navy text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-tight">
                {t.intro.title}
              </h2>
            </Reveal>
            <div className="mt-8 space-y-5 max-w-xl">
              {[t.intro.p1, t.intro.p2, t.intro.p3].map((p, i) => (
                <Reveal key={i} delay={0.1 + i * 0.06}>
                  <p className="font-sans text-charcoal/70 text-base sm:text-lg leading-relaxed">{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="overflow-hidden rounded-2xl shadow-[0_20px_60px_rgba(15,37,55,0.15)]">
                  <img src={IMGS.compassion} alt="Compassionate physician with patient" className="w-full h-[420px] object-cover" />
                </div>
                <div className="absolute -bottom-8 -left-4 sm:-left-8 bg-navy text-white rounded-2xl p-7 max-w-xs shadow-[0_20px_40px_rgba(15,37,55,0.25)]">
                  <p className="font-serif italic text-xl sm:text-2xl leading-snug text-champagne">
                    “{t.intro.highlight}”
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
};

/* ---------------- WHY REVIVE ---------------- */
const pillarIcons = [UserRound, FlaskConical, Layers, HeartHandshake];
export const WhyRevive = () => {
  const { t } = useSite();
  return (
    <section className="relative bg-navy text-white py-24 sm:py-32 overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-champagne/5 blur-3xl" />
      <Container>
        <Reveal><Chapter><span className="text-champagne/60">{t.why.chapter}</span></Chapter></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 max-w-2xl leading-tight">{t.why.title}</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
          {t.why.pillars.map((p, i) => {
            const Icon = pillarIcons[i];
            return (
              <Reveal key={i} delay={i * 0.08}>
                <div className="group h-full rounded-3xl border border-white/10 bg-white/[0.03] p-8 hover:bg-white/[0.06] hover:border-champagne/30 transition-colors duration-500">
                  <div className="w-12 h-12 rounded-full bg-champagne/15 grid place-items-center text-champagne mb-6 group-hover:scale-110 transition-transform duration-500">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-serif text-2xl mb-3">{p.t}</h3>
                  <p className="font-sans text-white/60 text-sm leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

/* ---------------- TREATMENTS ---------------- */
export const Treatments = () => {
  const { t, lang, openBooking } = useSite();
  const navigate = useNavigate();
  const imgs = [IMGS.cells, IMGS.room, IMGS.science];
  return (
    <section id="treatments" className="relative bg-offwhite py-24 sm:py-32">
      <Container>
        <div className="max-w-3xl">
          <Reveal><Chapter>{t.treatments.chapter}</Chapter></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-navy text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-tight">{t.treatments.title}</h2>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-16">
          {t.treatments.items.map((item, i) => (
            <Reveal key={i} delay={i * 0.1} className={i === 0 ? "md:col-span-2" : ""}>
              <button onClick={() => i === 0 ? navigate(lang === "es" ? "/es/terapia-con-celulas-madre" : "/stem-cell-therapy") : openBooking(item.tag)}
                className="group text-left w-full h-full rounded-3xl overflow-hidden bg-white border border-navy/5 shadow-[0_8px_30px_rgba(15,37,55,0.06)] hover:shadow-[0_20px_50px_rgba(15,37,55,0.13)] transition-shadow duration-500"
                data-testid={`treatment-${i}`}>
                <div className="relative overflow-hidden h-56">
                  <img src={imgs[i]} alt={item.t} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                  <span className="absolute top-4 left-4 text-xs tracking-[0.15em] uppercase bg-white/90 text-navy px-3 py-1.5 rounded-full font-sans">{item.tag}</span>
                </div>
                <div className="p-8">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-serif text-2xl sm:text-3xl text-navy leading-tight">{item.t}</h3>
                    <ArrowUpRight className="text-navy/40 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300 shrink-0 mt-1" size={22} />
                  </div>
                  <p className="font-sans text-charcoal/65 text-sm leading-relaxed mt-4">{item.d}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 font-sans text-sm text-navy/60 italic max-w-2xl border-l-2 border-champagne pl-4">{t.treatments.note}</p>
        </Reveal>
      </Container>
    </section>
  );
};

/* ---------------- PEPTIDES ---------------- */
export const Peptides = () => {
  const { t } = useSite();
  return (
    <section className="relative bg-navy text-white py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img src={IMGS.cells} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/70" />
      <Container className="relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal><Chapter><span className="text-champagne/60">{t.peptides.chapter}</span></Chapter></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-tight">{t.peptides.title}</h2>
            </Reveal>
            <Reveal delay={0.1}><p className="font-sans text-white/70 mt-6 leading-relaxed">{t.peptides.p1}</p></Reveal>
            <Reveal delay={0.15}><p className="font-sans text-white/60 text-sm mt-4 leading-relaxed">{t.peptides.p2}</p></Reveal>
            <Reveal delay={0.2}>
              <blockquote className="mt-8 font-serif italic text-2xl sm:text-3xl text-champagne leading-snug border-l-2 border-champagne/50 pl-6">
                “{t.peptides.quote}”
              </blockquote>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {t.peptides.areas.map((a, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-6 hover:border-champagne/40 transition-colors duration-400">
                  <span className="text-champagne/70 font-serif text-lg">{String(i + 1).padStart(2, "0")}</span>
                  <p className="font-sans text-sm text-white/80 mt-2">{a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

/* ---------------- NAD ---------------- */
export const NAD = () => {
  const { t } = useSite();
  return (
    <section className="relative bg-offwhite py-24 sm:py-32">
      <Container>
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Reveal>
              <div className="overflow-hidden rounded-2xl shadow-[0_20px_60px_rgba(15,37,55,0.12)]">
                <img src={IMGS.science} alt="Laboratory science" className="w-full h-[440px] object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal><Chapter>{t.nad.chapter}</Chapter></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-navy text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-tight">{t.nad.title}</h2>
            </Reveal>
            <Reveal delay={0.1}><p className="font-sans text-charcoal/70 text-lg mt-6 leading-relaxed max-w-xl">{t.nad.p1}</p></Reveal>
            <Reveal delay={0.15}><p className="font-sans text-charcoal/65 mt-4 leading-relaxed max-w-xl">{t.nad.p2}</p></Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
};

/* ---------------- EMERGING + MARQUEE ---------------- */
export const Emerging = () => {
  const { t } = useSite();
  return (
    <section id="science" className="relative bg-navy text-white py-24 sm:py-32 overflow-hidden">
      <div className="py-6 border-y border-white/10 mb-16">
        <Marquee speed={40} gradient={false} autoFill>
          {t.emerging.marquee.map((m, i) => (
            <span key={i} className="font-serif italic text-5xl sm:text-7xl text-white/10 mx-8">{m} ·</span>
          ))}
        </Marquee>
      </div>
      <Container>
        <div className="max-w-4xl">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05]">{t.emerging.title}</h2>
          </Reveal>
          <Reveal delay={0.1}><p className="font-sans text-white/70 text-lg mt-8 leading-relaxed">{t.emerging.p1}</p></Reveal>
          <Reveal delay={0.15}>
            <p className="font-serif italic text-3xl sm:text-4xl text-champagne mt-10">{t.emerging.philosophy}</p>
          </Reveal>
          <Reveal delay={0.2}><p className="font-sans text-white/60 text-sm mt-8 leading-relaxed max-w-2xl">{t.emerging.p2}</p></Reveal>
        </div>
      </Container>
    </section>
  );
};

/* ---------------- CONDITIONS ---------------- */
export const Conditions = () => {
  const { t, openBooking } = useSite();
  return (
    <section id="conditions" className="relative bg-offwhite py-24 sm:py-32">
      <Container>
        <div className="max-w-3xl">
          <Reveal><Chapter>{t.conditions.chapter}</Chapter></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-navy text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-tight">{t.conditions.title}</h2>
          </Reveal>
          <Reveal delay={0.1}><p className="font-sans text-charcoal/65 mt-6 leading-relaxed">{t.conditions.sub}</p></Reveal>
        </div>
        <div className="flex flex-wrap gap-3 mt-14">
          {t.conditions.list.map((c, i) => (
            <Reveal key={i} delay={Math.min(i * 0.03, 0.4)}>
              <button onClick={() => openBooking()}
                className="group inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white px-5 py-3 hover:bg-navy hover:border-navy transition-colors duration-300"
                data-testid={`condition-${i}`}>
                <span className="font-sans text-sm text-navy group-hover:text-white transition-colors duration-300">{c}</span>
                <ArrowUpRight size={15} className="text-navy/40 group-hover:text-champagne transition-colors duration-300" />
              </button>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <button onClick={() => openBooking()}
            className="mt-14 inline-flex items-center gap-2 rounded-full bg-navy text-white px-8 py-4 font-medium hover:bg-royal transition-colors duration-300"
            data-testid="conditions-explore">
            {t.conditions.cta}
            <ArrowUpRight size={17} />
          </button>
        </Reveal>
      </Container>
    </section>
  );
};

/* ---------------- REVIVE METHOD (scroll timeline) ---------------- */
export const ReviveMethod = () => {
  const { t } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 60%", "end 40%"] });
  const height = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  return (
    <section id="method" ref={ref} className="relative bg-navy text-white py-24 sm:py-32">
      <Container>
        <div className="max-w-3xl mb-16">
          <Reveal><Chapter><span className="text-champagne/60">{t.method.chapter}</span></Chapter></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-tight">{t.method.title}</h2>
          </Reveal>
        </div>
        <div className="relative pl-8 sm:pl-0">
          <div className="absolute left-1 sm:left-1/2 top-0 bottom-0 w-px bg-white/10 -translate-x-1/2" />
          <motion.div style={{ height }} className="absolute left-1 sm:left-1/2 top-0 w-px bg-champagne -translate-x-1/2 origin-top" />
          <div className="space-y-16 sm:space-y-24">
            {t.method.steps.map((s, i) => (
              <div key={i} className={`relative sm:grid sm:grid-cols-2 sm:gap-16 items-center ${i % 2 ? "sm:text-left" : "sm:text-right"}`}>
                <div className={`${i % 2 ? "sm:col-start-2" : "sm:col-start-1"}`}>
                  <Reveal>
                    <div className={`${i % 2 ? "" : "sm:ml-auto"} max-w-sm`}>
                      <span className="font-serif text-champagne text-5xl sm:text-6xl">{s.n}</span>
                      <h3 className="font-serif text-2xl sm:text-3xl mt-2">{s.t}</h3>
                      <p className="font-sans text-white/60 text-sm mt-3 leading-relaxed">{s.d}</p>
                    </div>
                  </Reveal>
                </div>
                <div className="absolute left-1 sm:left-1/2 top-2 sm:top-1/2 -translate-x-1/2 sm:-translate-y-1/2 w-3 h-3 rounded-full bg-champagne ring-4 ring-navy" />
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

/* ---------------- FINANCING ---------------- */
export const Financing = () => {
  const { t, openBooking, track } = useSite();
  return (
    <section className="relative bg-offwhite py-24 sm:py-32">
      <Container>
        <div className="rounded-3xl bg-gradient-to-br from-navy to-royal text-white p-10 sm:p-16 relative overflow-hidden">
          <div className="absolute -bottom-24 -right-24 w-[400px] h-[400px] rounded-full bg-champagne/10 blur-3xl" />
          <div className="relative max-w-2xl">
            <Reveal><Chapter><span className="text-champagne/60">{t.financing.chapter}</span></Chapter></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-tight">{t.financing.title}</h2>
            </Reveal>
            <Reveal delay={0.1}><p className="font-sans text-white/70 mt-6 leading-relaxed">{t.financing.p1}</p></Reveal>
            <Reveal delay={0.15}>
              <p className="font-serif italic text-xl sm:text-2xl text-champagne mt-6">{t.financing.highlight}</p>
            </Reveal>
            <Reveal delay={0.2}><p className="font-sans text-white/55 text-sm mt-4 leading-relaxed">{t.financing.p2}</p></Reveal>
            <Reveal delay={0.25}>
              <button onClick={() => { track("financing_cta_click"); openBooking(); }}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-champagne text-navy px-8 py-4 font-medium hover:bg-white transition-colors duration-300"
                data-testid="financing-cta">
                {t.financing.cta}
                <ArrowUpRight size={17} />
              </button>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
};

/* ---------------- INTERNATIONAL ---------------- */
export const International = () => {
  const { t, openWhatsApp } = useSite();
  return (
    <section id="international" className="relative bg-navy text-white py-24 sm:py-32 overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-25">
        <img src={IMGS.room} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy to-transparent" />
      </div>
      <Container className="relative">
        <div className="max-w-2xl">
          <Reveal><Chapter><span className="text-champagne/60">{t.intl.chapter}</span></Chapter></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-tight">{t.intl.title}</h2>
          </Reveal>
          <Reveal delay={0.1}><p className="font-sans text-white/70 mt-6 leading-relaxed">{t.intl.p1}</p></Reveal>
          <div className="mt-10 flex flex-wrap gap-x-3 gap-y-3">
            {t.intl.steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex items-center gap-2">
                  <span className="font-serif text-champagne text-lg">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-sans text-sm text-white/80">{s}</span>
                  {i < t.intl.steps.length - 1 && <span className="text-white/20 ml-1">—</span>}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <button onClick={() => openWhatsApp()}
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-8 py-4 font-medium hover:brightness-110 transition-[filter] duration-300"
              data-testid="intl-whatsapp">
              {t.intl.cta}
            </button>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};
